import { PACKING_LIST } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { isArrayAndHasValue } from "utils/functions";
import CreatePackingReq from "../CreatePackingReq";

const ViewPackingReq = () => {
  // States
  const [selectedPoEditData, setSelectedPoEditData] = useState(null);
  const [poMapId, setPoMapId] = useState(null);
  const [formData, setFormData] = useState(null);

  // Router
  const { id } = useParams();

  const getPOData = useCallback(async () => {
    const query = `${PACKING_LIST}/${id}/view/edit`;

    const response = await getData(query);

    if (response?.data?.code === 200) {
      const masterData = response?.data?.data;

      const packingRequirementDetails = masterData?.packing_requirement_details;

      setPoMapId(masterData?.po_map_id);

      // Set form data for PackingReqForm
      const poMaps = masterData?.po_maps || {};
      const formDataToSet = {
        original_po: poMaps?.original_po || null,
        internal_po: poMaps?.internal_po || null,
        buying_office_id: masterData?.buying_office_id || null,
        buying_office_name: masterData?.buying_office?.name || null,
        buyer_id: masterData?.buyer_id || null,
        buyer_name: masterData?.buyer?.name || null,
        sub_contract_factory_id: masterData?.sub_contract_factory_id || null,
        sales_contract_id: masterData?.sales_contract_id || null,
        reference_no: masterData?.sales_contract?.reference_no || null,
        po_map_id: masterData?.po_map_id || null,
        status: masterData?.status === "Approved" || masterData?.status === 1,
        packing_types: isArrayAndHasValue(poMaps?.packing_type_map)
          ? poMaps.packing_type_map.map((item) => item?.packing_type_id)
          : [],
        _packing_types: isArrayAndHasValue(poMaps?.packing_type_map)
          ? poMaps.packing_type_map.map((item) => ({
              packing_type_id: item?.packing_type_id,
              is_delete: 0,
              id: item?.id || 0,
            }))
          : [],
        p_o_type_name: poMaps?.po_type_info?.name || null,
        department_name: poMaps?.department || null,
      };
      setFormData(formDataToSet);

      if (isArrayAndHasValue(packingRequirementDetails)) {
        // Transform packing_requirement_details to expected carton format
        const customData = packingRequirementDetails.map((detail, index) => {
          // Group packing_size_ratios by color_id and sub_style_id
          const colorGroups = {};

          if (isArrayAndHasValue(detail?.packing_size_ratios)) {
            detail.packing_size_ratios.forEach((ratio) => {
              const styleId = ratio?.style_id || ratio?.style?.id || detail?.style?.id || detail?.style_id || null;
              const key = `${ratio.color_id}_${ratio.sub_style_id || "none"}_${styleId || "none"}`;
              if (!colorGroups[key]) {
                colorGroups[key] = {
                  color_id: ratio.color_id,
                  style_id: styleId,
                  sub_style_id: ratio.sub_style_id,
                  sizes: [],
                };
              }
              colorGroups[key].sizes.push({
                id: ratio.id,
                size_id: ratio.size_id,
                quantity: Number(ratio.qty) || 0,
              });
            });
          }

          // Convert colorGroups object to array
          const colors = Object.values(colorGroups).map((colorItem, colorIndex) => {
            const colorTotal = colorItem.sizes.reduce((result, sizeItem) => {
              return result + Number(sizeItem.quantity);
            }, 0);

            const cartonQty = Number(detail.carton_to) - Number(detail.carton_from) + 1;

            return {
              unique_id: colorIndex,
              color_id: colorItem.color_id,
              style_id: colorItem.style_id,
              sub_style_id: colorItem.sub_style_id,
              sizes: colorItem.sizes,
              color_total: colorTotal,
              req_sizes: colorItem.sizes.map((sizeItem) => {
                return {
                  size_id: sizeItem.size_id,
                  quantity: Number(sizeItem.quantity) * cartonQty || 0,
                };
              }),
              req_color_total: colorItem.sizes.reduce((result, sizeItem) => {
                return result + (Number(sizeItem.quantity) * cartonQty || 0);
              }, 0),
            };
          });

          // Calculate carton_qty from carton_from and carton_to
          const cartonQty = Number(detail.carton_to) - Number(detail.carton_from) + 1;

          // Calculate total_quantity from packing_size_ratios
          const totalQuantity = isArrayAndHasValue(detail?.packing_size_ratios)
            ? detail.packing_size_ratios.reduce((result, ratio) => {
                return result + Number(ratio.qty || 0);
              }, 0)
            : 0;

          // Calculate carton_pack_req_qty (total quantity * carton_qty)
          const cartonPackReqQty = totalQuantity * cartonQty;

          // Calculate dimensions and weights
          const length = Number(detail.commercial_length || detail.initial_length || 0);
          const width = Number(detail.commercial_width || detail.initial_width || 0);
          const height = Number(detail.commercial_height || detail.initial_height || 0);
          const cbm = Number(detail.commercial_cbm || detail.initial_cbm || 0);
          const gross = Number(detail.commercial_gross_weight || detail.initial_gross_weight || 0);
          const net = Number(detail.commercial_net_weight || detail.initial_net_weight || 0);

          // Calculate total_cbm (cbm * carton_qty)
          const totalCbm = cbm * cartonQty;

          return {
            id: detail.id,
            unique_id: index,
            colors: colors,
            carton_qty: cartonQty,
            assortment: detail.assortment || null,
            item: detail.item || null,
            upc: detail.upc || null,
            length: length,
            width: width,
            height: height,
            cbm: cbm,
            total_cbm: totalCbm,
            gross: gross,
            net: net,
            total_gross: gross * cartonQty,
            total_net: net * cartonQty,
            style_id: detail?.packing_size_ratios?.[0]?.style_id || detail?.style?.id || detail?.style_id || null,
            start_range: Number(detail.carton_from),
            end_range: Number(detail.carton_to),
            carton_pack_req_qty: cartonPackReqQty,
            total_quantity: totalQuantity,
            customer_po: detail.customer_po || null,
          };
        });

        setSelectedPoEditData(customData);
      }
    } else {
      alertPop("error", "Error", response?.data?.message?.[0] || "Something went wrong!");
    }
  }, [id]);

  useEffect(() => {
    // Get PO data on first render
    if (id) {
      getPOData();
    }
  }, [id, getPOData]);

  return (
    <div>
      <CreatePackingReq
        selectedPoEditData={selectedPoEditData}
        poMapId={poMapId}
        isEdit={true}
        isView={true}
        editId={Number(id)}
        formData={formData}
      />
    </div>
  );
};

export default ViewPackingReq;
